import CardPayment from "@/assets/boarding-tour/card-payment.json";
import HappyMan from "@/assets/boarding-tour/medal.json";
import MoneyTransfer from "@/assets/boarding-tour/money-transfer.json";
import GreenBackgroundShape from "@/components/ui/GreenBackgroundShape";
import {
	Carousel,
	CarouselApi,
	CarouselContent,
	CarouselItem,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import {
	useNavigate,
	Link,
	useLoaderData,
	useSearch,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import Lottie, { LottieProps } from "react-lottie";
import { Button } from "../../components/ui/button/button";
import { buttonVariants } from "@/components/ui/button/button";

const WelcomeMessagePage = () => {
	const navigate = useNavigate();
	const [currentSlide, setCurrentSlide] = useState(0);
	const [api, setApi] = useState<CarouselApi>();
	const [isTransitioning, setIsTransitioning] = useState(false);
	const promo = useSearch({ from: "/welcome-message" });
	const promoCode = promo ? promo.promo : undefined;
	const { name } = useLoaderData({ from: "__root__" });

	useEffect(() => {
		if (!api) {
			return;
		}

		setCurrentSlide(api.selectedScrollSnap());

		api.on("select", () => {
			setCurrentSlide(api.selectedScrollSnap());
		});
	}, [api]);

	const defaultOptions = {
		loop: true,
		autoplay: true,
		animationData: CardPayment,
		rendererSettings: {
			preserveAspectRatio: "xMidYMid slice",
		},
	};

	const defaultOptions2: LottieProps = {
		options: {
			loop: true,
			autoplay: true,
			animationData: MoneyTransfer,
			rendererSettings: {
				preserveAspectRatio: "xMidYMid slice",
			},
		},
	};

	const happyManConfig = {
		loop: true,
		autoplay: true,
		animationData: HappyMan,
		rendererSettings: {
			preserveAspectRatio: "xMidYMid slice",
		},
	};

	const handleNext = () => {
		if (currentSlide < 2) {
			setIsTransitioning(true);
			api?.scrollNext();
			setTimeout(() => {
				setIsTransitioning(false);
			}, 200);
		}
	};

	const isFirstSlide = currentSlide === 0;
	const isSecondSlide = currentSlide === 1;
	const isThirdSlide = currentSlide === 2;

	return (
		<div className="flex flex-col justify-between bg-white text-textDark h-[100dvh] max-w-[800px] w-full mx-auto">
			<div className="relative flex items-center justify-center">
				<GreenBackgroundShape shapeType="sharp">
					{/* Centered container for all Lottie animations */}
					<div className="inset-0 flex items-center justify-center">
						<div className="flex items-center justify-center w-full max-w-sm aspect-[10/7] relative">
							<div
								className={cn("absolute flex items-center justify-center", {
									hidden: !isFirstSlide,
									"opacity-0": !isFirstSlide,
									"animate-fadeIn": isFirstSlide,
								})}
							>
								<Lottie
									style={{
										maxWidth: 400,
										width: "100%",
										height: "auto",
									}}
									options={defaultOptions}
								/>
							</div>

							<div
								className={cn("absolute flex items-center justify-center", {
									hidden: !isSecondSlide,
									"opacity-0": !isSecondSlide,
									"animate-fadeIn": isSecondSlide,
								})}
							>
								<Lottie
									style={{
										maxWidth: 400,
										width: "100%",
										height: "auto",
									}}
									options={defaultOptions2.options}
								/>
							</div>

							<div
								className={cn(
									"absolute flex items-center bg-secondary rounded-full aspect-square justify-center",
									{
										hidden: !isThirdSlide,
										"opacity-0": !isThirdSlide,
										"animate-fadeIn": isThirdSlide,
										"animate-fadeOut": !isThirdSlide,
									},
								)}
							>
								<Lottie
									style={{
										maxWidth: 250,
										width: "100%",
										height: "auto",
									}}
									options={happyManConfig}
								/>
							</div>
						</div>
					</div>
				</GreenBackgroundShape>
			</div>

			<div
				className={`px-5 w-[98%] transition-opacity duration-300 ease-in-out ${
					isTransitioning ? "opacity-0" : "opacity-100"
				}`}
			>
				<Carousel
					data-testid="sign-up-carousel"
					setApi={setApi}
					className="w-full"
					opts={{
						align: "start",
						duration: 20,
						loop: false,
					}}
				>
					<CarouselContent>
						<CarouselItem>
							<div
								className={cn("w-full mt-8", {
									"animate-fadeIn": currentSlide === 0,
									"animate-fadeOut": currentSlide !== 0,
								})}
							>
								<div className="text-4xl text-textDark font-bold text-center">
									<h1>
										Connect Your <br /> Card. Unlock Cash.
									</h1>
								</div>
								<div className="mt-6 text-sm text-textDark font-medium text-center opacity-80">
									<p>Connect your card and load the</p>
									<p>money into your {name} account.</p>
								</div>
							</div>
						</CarouselItem>
						<CarouselItem>
							<div
								className={cn("w-full mt-8", {
									"animate-fadeIn": currentSlide === 1,
									"animate-fadeOut": currentSlide !== 1,
								})}
							>
								<div className="text-4xl font-bold text-textDark text-center">
									<h1>Move Your Funds </h1>
									<h1>With Ease.</h1>
								</div>
								<div className="mt-6 text-sm font-medium text-center text-textDark">
									<p>Send money from {name} to your bank, debit</p>
									<p>card, or friends. Total control — no surprises.</p>
								</div>
							</div>
						</CarouselItem>
						<CarouselItem>
							<div
								className={cn("w-full mt-8", {
									"animate-fadeIn": currentSlide === 2,
									"animate-fadeOut": currentSlide !== 2,
								})}
							>
								<div className="text-4xl font-bold text-textDark text-center">
									<h1>
										Ready to Try <br /> {name}?
									</h1>
								</div>
								<div className="mt-6 text-sm font-medium text-center text-textDark">
									<p>{name} creates accessibility. Your rent, bills,</p>
									<p>
										opportunities — covered. It's your credit. Use it your way.
									</p>
								</div>
							</div>
						</CarouselItem>
					</CarouselContent>

					{/* Custom dots indicator - now shows on all slides */}
					<div className="flex justify-center my-4 space-x-2">
						{[0, 1, 2].map((index) => (
							<Button
								key={index}
								size="compact"
								className={`w-2 h-2 rounded-full transition-colors ${
									currentSlide === index ? "bg-primary-600" : "bg-primary-200"
								}`}
								onClick={() => api?.scrollTo(index)}
							/>
						))}
					</div>
				</Carousel>
			</div>

			<div
				className={`flex justify-between px-5 mb-12 transition-opacity duration-300 ease-in-out ${
					isTransitioning ? "" : "opacity-100"
				}`}
			>
				{currentSlide < 2 ? (
					<>
						<Link
							to="/sign-up"
							search={{ promo: promoCode }}
							className={buttonVariants({
								variant: "link",
								className: "opacity-40",
							})}
						>
							Skip
						</Link>
						<Button
							variant="link"
							className="relative z-50 px-3 text-base font-bold text-textDark"
							onClick={handleNext}
						>
							Next
						</Button>
					</>
				) : (
					<Button
						onClick={() => {
							navigate({ to: "/sign-up", search: { promo: promoCode } });
						}}
						className={cn("w-full", {
							"animate-fadeIn": currentSlide === 2,
						})}
						variant="secondary"
						data-testid="create-account-button"
					>
						Create Account
					</Button>
				)}
			</div>

			<style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
      `}</style>
		</div>
	);
};

export default WelcomeMessagePage;
